import { AlertTriangle, Check, X } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { RandomDotmSpinner } from '../../../components/ui/RandomDotmSpinner';
import { cn } from '../../../lib/utils';
import { ConnectionErrorNotice } from './ConnectionErrorNotice';
import { TestConnectionStepper } from './TestConnectionStepper';
import type { ConnectionErrorFixRetry } from '../utils/connectionErrorFix';
import type { ResultStripModel } from '../connection-dialog/pane/resultStripModel';
import { redactTargetSecrets } from '../../../types/connectionTransfer';

type Props = {
  /** What the strip says (`resultStripModel`), or null when only the watchdog shows. */
  model: ResultStripModel | null;
  isEditMode: boolean;
  /** A test run (or delete) is in flight: fix chips and Save anyway wait for it. */
  busy: boolean;
  onLegAction: (actionId: string) => void;
  onApplyFix: (fix: ConnectionErrorFixRetry) => void;
  onSaveAnyway: () => void;
  /** The X on a finished result: hides it until the next answer (a new test shows the strip again). */
  onDismiss?: () => void;
  watchdog: {
    visible: boolean;
    /** Save anyway is offered only when the stuck run is a Save's test. */
    offerSaveAnyway: boolean;
    onSaveAnyway: () => void;
    onKeepWaiting: () => void;
    onCancel: () => void;
    deleting: boolean;
  };
  /** The short tier shows the strip inside the form's scroll region (Details): no cap, no band of its own. */
  inline?: boolean;
};

const APPEAR = 'animate-in fade-in slide-in-from-bottom-2 duration-200 motion-reduce:animate-none';

/**
 * The docked result strip (Option C, C2, rule 3b.7): one component for every engine and every test path, in the pane
 * above the footer. In order: the answer (a success line, a calm warning, or the error notice with its fix chip), the
 * legs as chips where the backend reports legs (SSH tunnel + database; one per Azure Storage service, led by their
 * summary line), and Save anyway / Update anyway worded for the error — or, for a validation or security refusal of the
 * settings, why it is not offered (C2 review C2-01 / C2-02). "Testing the connection…" while a test runs.
 * A finished result has an X that closes it; the next test's answer opens the strip again.
 * The strip is the live region (`role="status"`).
 *
 * UI review round B U1 / C2 R4 / N1: the region never shrinks with the form (the form gives up height first, down to its
 * 10rem), and its cap leaves room for that minimum plus the dialog's header and footer (22rem) inside the dialog's
 * height — `min(680px,90vh)`, the same expression as the dialog's `h-[…]` in NewConnectionPopup — so a complete result
 * and its action fit at 1024×700; long answers scroll inside it. The cap used to be measured against 90vh alone: above
 * an ~877 px viewport 40vh outgrew what a 680-px dialog has left, and a long result pushed Test connection / Cancel /
 * Save off the dialog (release pass, real Azure, 1600×1000).
 */
export const RESULTS_REGION_CLASS = 'flex shrink-0 max-h-[min(40vh,calc(min(680px,90vh)-22rem))] flex-col gap-2 overflow-y-auto border-t bg-muted/30 px-6 py-2.5';

function Notice({ model }: { model: ResultStripModel }) {
  const { notice, legsSummary } = model;
  if (legsSummary) {
    // The check means every service verified (tone `success`); a partial run — a service not verified or refused — and a
    // failed one lead with the warning (release pass: "…not fully verified" read as a success).
    return (
      <p className="flex items-start gap-2 text-sm font-medium" data-testid="connection-test-legs-summary">
        {model.tone !== 'success' ? <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-warning" aria-hidden /> : <Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" aria-hidden />}
        <span className="min-w-0 break-words">{legsSummary.text}</span>
      </p>
    );
  }
  if (notice?.kind === 'success') {
    return (
      <div className="flex items-start gap-2 rounded-md bg-primary/10 px-3 py-2 text-sm text-primary">
        <Check className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
        <span className="min-w-0 break-words">{redactTargetSecrets(notice.text)}</span>
      </div>
    );
  }
  if (notice?.kind === 'warning') {
    return (
      <div className="flex items-start gap-2 rounded-md border border-warning/30 bg-warning/10 px-3 py-2 text-sm text-foreground" data-testid="connection-test-warning">
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-warning" aria-hidden />
        <span className="min-w-0 break-words">{redactTargetSecrets(notice.text)}</span>
      </div>
    );
  }
  return null;
}

export function ConnectionTestResults({ model, isEditMode, busy, onLegAction, onApplyFix, onSaveAnyway, onDismiss, watchdog, inline = false }: Props) {
  if (!model && !watchdog.visible) return null;
  return (
    <div
      className={inline ? 'mb-3 flex flex-col gap-2 rounded-md border bg-muted/30 p-3' : RESULTS_REGION_CLASS}
      role="status"
      data-testid="connection-test-results"
      data-tone={model?.tone}
    >
      {model?.tone === 'progress' && (
        <p className="flex items-center gap-2 text-sm text-muted-foreground" data-testid="connection-test-progress">
          <RandomDotmSpinner size={16} />
          {model.summary}
        </p>
      )}
      {model && model.tone !== 'progress' && (
        <div className={cn('flex items-start gap-1', APPEAR)}>
          <div className="flex min-w-0 flex-1 flex-col gap-2" data-testid="connection-test-result">
            <Notice model={model} />
            {model.notice?.kind === 'error' && (
              <ConnectionErrorNotice
                error={model.notice.error}
                connection={model.notice.connection}
                errorCode={model.notice.errorCode}
                origin={model.notice.origin}
                title={model.notice.title}
                surface="connection-dialog"
                onApplyFix={onApplyFix}
                fixBusy={busy}
              />
            )}
            {model.legs && (
              <div data-testid="connection-test-stepper-banner">
                <TestConnectionStepper legs={model.legs} onLegAction={onLegAction} />
              </div>
            )}
            {model.saveAnywayWithheld && (
              <p className="break-words rounded-md border border-border bg-background px-3 py-1.5 text-xs text-muted-foreground" data-testid="connection-save-anyway-withheld">
                {model.saveAnywayWithheld}
              </p>
            )}
            {model.saveAnyway && (
              <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 rounded-md border border-border bg-background py-1.5 pl-3 pr-1.5" data-testid="connection-save-anyway">
                <span className="min-w-0 text-xs text-muted-foreground">{model.saveAnyway}</span>
                <Button type="button" variant="outline" size="xs" onClick={onSaveAnyway} disabled={busy} className="shrink-0" data-testid="save-connection-anyway-button">
                  {isEditMode ? 'Update anyway' : 'Save anyway'}
                </Button>
              </div>
            )}
          </div>
          {onDismiss && (
            <Button type="button" variant="ghost" size="icon" className="-mr-1 -mt-1 h-7 w-7 shrink-0 text-muted-foreground" aria-label="Close the test result" onClick={onDismiss} data-testid="connection-test-results-dismiss">
              <X className="h-4 w-4" aria-hidden />
            </Button>
          )}
        </div>
      )}

      {watchdog.visible && (
        <div className={cn('space-y-2 rounded-md border border-warning/30 bg-warning/10 p-3 text-sm text-foreground', APPEAR)} data-testid="connection-test-watchdog">
          <div className="flex items-start gap-2">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-warning" aria-hidden />
            <span>Still trying to connect… This server is taking longer than usual to answer.</span>
          </div>
          <div className="flex flex-wrap items-center justify-end gap-2">
            {watchdog.offerSaveAnyway && (
              <Button type="button" variant="outline" size="sm" onClick={watchdog.onSaveAnyway} disabled={watchdog.deleting} data-testid="test-watchdog-save-anyway-button">
                {isEditMode ? 'Update anyway' : 'Save anyway'}
              </Button>
            )}
            <Button type="button" variant="ghost" size="sm" onClick={watchdog.onKeepWaiting} data-testid="test-watchdog-keep-waiting-button">
              Keep waiting
            </Button>
            <Button type="button" variant="outline" size="sm" onClick={watchdog.onCancel} data-testid="test-watchdog-cancel-button">
              Cancel test
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
